import React, { memo, Fragment } from "react";
import * as S from "sanctuary";
import * as R from "ramda";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone"; // dependent on utc plugin; order matters
import advanced from "dayjs/plugin/advancedFormat";
import { Cluster, Text, AutopayIcon } from "@thecb/components";
import { COLOR_SOLID } from "@/constants/colors";
import { toFormattedDate } from "@/util/dates";
import {
  Table,
  TableHeader,
  TableHeading,
  TableBody,
  TableRow,
  TableRowEntry,
} from "@/components/generic/NewTable";
import { makeFakeRow } from "@/components/generic/FakeRow";
import { RESULTS_PER_PAGE } from "./TransactionSearchResults.state";
import { getAmountByStatus } from "@/components/payment/payment.util";
import { NotAvailable } from "./TractionSearchResults.styled";
import {
  FILTERS,
  PAYMENT_STATUS_FILTER,
} from "@/components/transactionSearchPage/FiltersForm";
import { checkAccountNumber } from "@/util/regex";
import { PAYMENT_INITIATOR_SCHEDULE_SERVICE } from "./TransactionSearchResults.state";
import PaymentTypeWithIcon from "./PaymentTypeWithIcon";

const getStatusDescription = S.pipe([
  (status) => S.find((e) => e.value === status)(FILTERS[PAYMENT_STATUS_FILTER]),
  S.fromMaybe({ text: "Unknown" }),
  S.prop("text"),
]);

// These are necessary to display the abbreviated Timezone in the table header
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advanced);

const FakeRow = makeFakeRow([90, 60, 110, 150, 60, 100, 90]);
const FakeRowSecondary = makeFakeRow([100, 120, 150, 150, 90, 150, 100]);

const PaymentRow = ({
  id,
  agency,
  accountNumber,
  createdAt,
  paymentType,
  paymentInitiator,
  creditCard,
  amount,
  status,
  bankAccount,
  openTransactionDetails,
}) => {
  return (
    <TableRow onClick={() => openTransactionDetails(id)} data-qa={id}>
      <TableRowEntry data-qa="td-id">{id}</TableRowEntry>
      <TableRowEntry data-qa="td-agency">{agency}</TableRowEntry>
      <TableRowEntry data-qa="td-account-number">
        {checkAccountNumber(accountNumber) || !accountNumber ? (
          <NotAvailable>Not available</NotAvailable>
        ) : (
          accountNumber
        )}
      </TableRowEntry>
      <TableRowEntry data-qa="td-created-at">{createdAt}</TableRowEntry>
      <TableRowEntry data-qa="td-payment-method">
        <PaymentTypeWithIcon
          creditCard={creditCard}
          bankAccount={bankAccount}
          paymentType={paymentType}
        />
      </TableRowEntry>
      <TableRowEntry data-qa="td-status">
        <Cluster justify="flex-start" align="center" childGap="0.25rem" nowrap>
          <Text variant="pS">{getStatusDescription(status)}</Text>
          {paymentInitiator &&
          paymentInitiator.toLowerCase() === PAYMENT_INITIATOR_SCHEDULE_SERVICE ? (
            <AutopayIcon
              fill={COLOR_SOLID}
              style={{ width: "18px", height: "18px" }}
            />
          ) : null}
        </Cluster>
      </TableRowEntry>
      <TableRowEntry extraStyles="text-align: right" data-qa="td-amount">
        {amount}
      </TableRowEntry>
    </TableRow>
  );
};

const TransactionsTable = ({
  transactions,
  openTransactionDetails,
  isLoading,
  userTimezone,
}) => (
  <Table>
    <TableHeader>
      <TableHeading>Transaction ID</TableHeading>
      <TableHeading>Agency</TableHeading>
      <TableHeading>Account number</TableHeading>
      <TableHeading>
        Transactions date (
        {S.isJust(userTimezone) ? dayjs().tz(userTimezone.value).format("z") : "???"}
        )
      </TableHeading>
      <TableHeading>Payment method</TableHeading>
      <TableHeading>Status</TableHeading>
      <TableHeading extraStyles="text-align: right">Amount</TableHeading>
    </TableHeader>
    <TableBody>
      {isLoading
        ? Array(RESULTS_PER_PAGE / 2)
            .fill()
            .map((_, index) => (
              <Fragment key={index}>
                {FakeRow}
                {FakeRowSecondary}
              </Fragment>
            ))
        : transactions.map(
            (
              {
                id,
                createdAt,
                fundedAt,
                status,
                type,
                paymentInitiator,
                creditCard,
                subtotal,
                bankAccount,
                agency,
                account,
                originalPaymentId,
              },
              index
            ) => (
              <PaymentRow
                key={S.fromMaybe(index)(id)}
                id={S.fromMaybe(0)(id)}
                accountNumber={R.pathOr(null, ["value", "number", "value"], account)}
                agency={S.fromMaybe("")(agency)}
                createdAt={toFormattedDate(createdAt, userTimezone)}
                fundedAt={toFormattedDate(fundedAt, userTimezone)}
                paymentType={S.fromMaybe("")(type)}
                paymentInitiator={S.fromMaybe("")(paymentInitiator)}
                creditCard={creditCard}
                amount={
                  S.isNothing(subtotal)
                    ? "unknown"
                    : getAmountByStatus(
                        subtotal.value,
                        S.fromMaybe(S.Left("unknown"))(status).value,
                        originalPaymentId.value
                      )
                }
                status={S.fromEither("")(S.fromMaybe(S.Right(""))(status))}
                bankAccount={bankAccount}
                openTransactionDetails={openTransactionDetails}
              />
            )
          )}
    </TableBody>
  </Table>
);

export default memo(TransactionsTable);
